/**
 * V210.2: useEntitlements Hook
 * Centralized hook for accessing user entitlements
 * 
 * Usage:
 * const { entitlements, loading, refresh } = useEntitlements(userId);
 * if (entitlements?.isVerified) {
 *   // Show verified badge
 * }
 */

import { useState, useEffect, useCallback } from 'react';
import { EntitlementService, type Entitlements } from '@/services/entitlement/EntitlementService';

interface UseEntitlementsResult {
  entitlements: Entitlements | null;
  loading: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
}

/**
 * Hook to get user entitlements
 * Automatically loads on mount and when userId changes
 * 
 * @param userId - User ID to get entitlements for (null/undefined = no entitlements)
 * @returns Entitlements object, loading state, error, and refresh function
 */
export function useEntitlements(userId: string | null | undefined): UseEntitlementsResult {
  const [entitlements, setEntitlements] = useState<Entitlements | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const loadEntitlements = useCallback(async (forceRefresh = false) => {
    if (!userId) {
      setEntitlements(null);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await EntitlementService.resolveEntitlements(userId, forceRefresh);
      setEntitlements(result);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to load entitlements');
      setError(error);
      console.error('❌ V210.2: Error loading entitlements:', error);
      // Fail safe: return no entitlements on error
      setEntitlements({
        isVerified: false,
        verifiedStatus: null,
        provider: null,
        trustScoreBonus: 0,
      });
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    loadEntitlements();
  }, [loadEntitlements]);

  const refresh = useCallback(() => {
    return loadEntitlements(true); // Force refresh bypasses cache
  }, [loadEntitlements]);

  return {
    entitlements,
    loading,
    error,
    refresh,
  };
}

export default useEntitlements;
